import { AsyncPipe, JsonPipe, NgIf, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal, Signal, untracked } from '@angular/core';
import { IonicModule, NavController } from '@ionic/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { TuiMobileDialogService, TuiSheetModule } from '@taiga-ui/addon-mobile';
import { tuiIsPresent, TuiLetModule } from '@taiga-ui/cdk';
import { TuiButtonModule, TuiDialogContext, TuiSvgModule } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@tinkoff/ng-polymorpheus';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { selectCartReceiver, selectCartShop, selectCartTable, selectOrderType } from '@us/web/cart/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { OrdersApiService } from '@us/web/orders/data-access';
import { DeliveryAddress, FormattedAddressPipe, OrderShop, OrderType, Table } from '@us/web/shared/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MessageService } from '@us/web/shared/feature';
import { getErrorMessage, RouteUtil } from '@us/web/shared/utils';
import {
    selectAllowDelivery,
    selectRequiredToLogin,
    selectAllowOrderAtTheTable,
    selectAllowPickup,
    selectAllowTakeAway,
    selectOrderTypes,
    selectShopId
} from '@us/web/shop/data-access';
import { ShopsActions } from '@us/web/shops/data-access';
import { BarcodeFormat, Exception, Result } from '@zxing/library';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { filter, take, tap } from 'rxjs';
import { TuiBlockStatusModule } from '@taiga-ui/layout';

@Component({
    selector: 'us-order-type-selector',
    standalone: true,
    imports: [
        TuiSvgModule,
        TranslocoModule,
        TuiButtonModule,
        ZXingScannerModule,
        NgIf,
        AsyncPipe,
        JsonPipe,
        TuiSheetModule,
        IonicModule,
        FormattedAddressPipe,
        TuiLetModule,
        TuiBlockStatusModule,
        NgOptimizedImage
    ],
    templateUrl: './order-type-selector.component.html',
    styleUrls: ['./order-type-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderTypeSelectorComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly messageService = inject(MessageService);
    private readonly translocoService = inject(TranslocoService);
    private readonly ordersService = inject(OrdersApiService);
    private readonly mobileDialogService = inject(TuiMobileDialogService);
    private readonly navController = inject(NavController);

    private readonly context = inject(POLYMORPHEUS_CONTEXT) as TuiDialogContext<{
        orderType: OrderType;
        table?: Table;
        isChanged: boolean;
    }>;

    readonly OrderType = OrderType;
    readonly allowedFormats = [BarcodeFormat.QR_CODE];

    readonly shop: Signal<OrderShop | undefined> = this.store.selectSignal(selectCartShop);
    readonly cartOrderType: Signal<OrderType | null> = this.store.selectSignal(selectOrderType);
    readonly receiver: Signal<DeliveryAddress | null> = this.store.selectSignal(selectCartReceiver);
    readonly orderTypes: Signal<OrderType[]> = this.store.selectSignal(selectOrderTypes);
    readonly allowDelivery = this.store.selectSignal(selectAllowDelivery);
    readonly allowPickup = this.store.selectSignal(selectAllowPickup);
    readonly allowTakeAway = this.store.selectSignal(selectAllowTakeAway);
    readonly allowOrderAtTheTable = this.store.selectSignal(selectAllowOrderAtTheTable);
    readonly allowLogin = this.store.selectSignal(selectRequiredToLogin);
    readonly selectedTable = this.store.selectSignal(selectCartTable);

    readonly selectedOrderType = signal<OrderType | null>(null);
    checking = false;
    readonly camerasNotFound = signal(false);
    readonly hasPermission = signal<boolean | null>(null);

    private orderTypesEffect = effect(
        () => {
            const orderTypes = this.orderTypes();
            if (orderTypes.length === 1) {
                this.handleSelect(orderTypes[0]);
            }
        },
        { allowSignalWrites: true }
    );

    private orderTypeEffect = effect(() => {
        const orderType = this.cartOrderType();
        const selectedTable = untracked(this.selectedTable);
        if (orderType === OrderType.OrderAtTheTable && !!selectedTable) {
            this.context.completeWith({
                orderType: orderType as OrderType,
                table: selectedTable,
                isChanged: false
            });
            return;
        }
    });

    ngOnInit() {
        if (!this.shop()) {
            this.store
                .select(selectShopId)
                .pipe(
                    filter(tuiIsPresent),
                    take(1),
                    tap(shopId => {
                        this.store.dispatch(ShopsActions.selectShop({ shopId }));
                    })
                )
                .subscribe();
        }
    }

    handleSelect(orderType: OrderType, update = false) {
        this.selectedOrderType.set(orderType);
        if (orderType !== OrderType.OrderAtTheTable) {
            this.context.completeWith({ orderType, isChanged: true });
            return;
        }

        this.store
            .select(selectCartTable)
            .pipe(
                take(1),
                tap(table => {
                    if (table && !update) {
                        this.context.completeWith({ orderType, isChanged: true });
                    }
                })
            )
            .subscribe();
    }

    camerasFoundHandler(event: MediaDeviceInfo[]) {
        console.log('camerasFoundHandler', event);
    }

    camerasNotFoundHandler(event: any) {
        this.camerasNotFound.set(true);
    }

    scanSuccessHandler(event: string) {
        console.log('scanSuccessHandler', event);
    }

    scanErrorHandler(event: Error) {
        console.log('scanErrorHandler', event);
    }

    scanFailureHandler(event: Exception | undefined) {
        console.log('scanFailureHandler', event);
    }

    scanCompleteHandler(result: Result) {
        if (this.checking) {
            return;
        }

        const url = new URL(result.getText());
        const tableId = Number(url.searchParams.get('tableId'));
        if (tableId) {
            // TODO: implement tableId validation
            // this.store.dispatch(CartActionsV2.selectTable({ tableId }));
            this.checking = true;
            this.ordersService.validateTable({ tableId }).subscribe({
                next: table => {
                    this.checking = false;
                    this.messageService.info(this.translocoService.translate('chose_table') + ' ' + table.name);
                    this.context.completeWith({ orderType: OrderType.OrderAtTheTable, table, isChanged: true });
                },
                error: error => {
                    this.checking = false;
                    this.mobileDialogService
                        .open(getErrorMessage(error), {
                            label: this.translocoService.translate('notification'),
                            actions: [this.translocoService.translate('ok')]
                        })
                        .subscribe();
                }
            });
        }
    }

    async handleChangeReceiver(event: MouseEvent) {
        event.stopPropagation();
        this.handleSelect(OrderType.Delivery);
        await this.navController.navigateForward(RouteUtil.getSelectDeliveryAddressRouteUrl(), {
            queryParams: { returnUrl: RouteUtil.getHomeRouteUrl() }
        });
    }

    async handleChangeShop(event: MouseEvent, orderType: OrderType) {
        event.stopPropagation();
        this.handleSelect(orderType);
        await this.navController.navigateForward(RouteUtil.getSelectStoreRouteUrl(), {
            queryParams: { returnUrl: RouteUtil.getHomeRouteUrl() }
        });
    }

    handleChangeTable(event: MouseEvent) {
        event.stopPropagation();
        this.handleSelect(OrderType.OrderAtTheTable, true);
    }

    permissionResponseHandler(value: boolean) {
        this.hasPermission.set(value);
    }
}
